import React from 'react';
import Cleave from 'cleave.js';
import ReactDom from 'react-dom';

import InputText from 'design-system/InputText';

// change is fired by Cleave, but react complains onChange handler not being passed with `value` prop
// passing this silences the warning
const placeholderInputChange = () => undefined;

interface NumericInputProps {
  value: number;
  style?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  onChange: (value: number) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
  positiveOnly?: boolean;
  decimalPlaces?: number;
  autosize?: boolean;
  size: 'sm' | 'md' | 'lg';
  suggestedMax?: number;
  max?: number;
  min?: number;
}

/** TODO refactor to use `design-system/TextInput` */
const NumericInput: React.FC<NumericInputProps> = props => {
  const cleaveInstance = React.useRef<Cleave | null>(null);
  const [rawValue, setRawValue] = React.useState(props.value.toString());
  const [formattedValue, setFormattedValue] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // We don't want onChange to be fired in any situation unless the user
  // is manually inputting. So we need to have focus management to detect
  // that. e.g when coordinates change due to resizing in 3d scene.
  const inputFocused = React.useRef(false);

  React.useEffect(() => {
    // eslint-disable-next-line react/no-find-dom-node
    const inputEl = ReactDom.findDOMNode(inputRef.current);
    if (!inputEl || !(inputEl instanceof HTMLElement)) {
      return;
    }

    cleaveInstance.current = new Cleave(inputEl, {
      onValueChanged(e: React.ChangeEvent<HTMLInputElement>) {
        setFormattedValue(e.target.value);
        setRawValue((e.target as any).rawValue);
      },
      numeral: true,
      numeralThousandsGroupStyle: 'thousand',
      numeralPositiveOnly: props.positiveOnly,
      numeralDecimalScale: props.decimalPlaces != null ? props.decimalPlaces : 2,
    });
    return () => {
      // @ts-ignore FIXME - Typescript Strict [2531]
      cleaveInstance.current.destroy();
    };
  }, [props.positiveOnly, props.decimalPlaces]);

  function syncWithExternalValue() {
    // Could cause infinite re-render if we don't check here

    // @ts-ignore FIXME - Typescript Strict [2531]
    if (cleaveInstance.current.getRawValue() !== props.value.toString()) {
      // @ts-ignore FIXME - Typescript Strict [2531]
      cleaveInstance.current.setRawValue(props.value.toString());
    }
  }

  // Make sure input is synced when values are changed from outside
  // e.g when coordinates change due to resizing in 3d scene.
  React.useEffect(syncWithExternalValue, [props.value]);

  const handleBlur = () => {
    inputFocused.current = false;
    const newValue = parseFloat(rawValue);

    if (props.suggestedMax && props.suggestedMax < newValue) {
      const confirm = window.confirm(
        `Are you sure the dimension is ${newValue}? Large enough dimension could cause the app to crash.`,
      );

      if (!confirm) {
        return syncWithExternalValue();
      }
    }

    if (
      (props.min !== undefined && props.min > newValue) ||
      (props.max !== undefined && props.max < newValue)
    ) {
      return syncWithExternalValue();
    }
    props.onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Enter') {
      return false;
    }
    handleBlur();
    return true;
  };
  return (
    <InputText
      disabled={!!props.disabled}
      autosize={props.autosize}
      style={props.style}
      containerStyle={props.containerStyle}
      size={props.size}
      inputRef={node => {
        inputRef.current = node;
      }}
      value={formattedValue}
      onChange={placeholderInputChange}
      onFocus={() => (inputFocused.current = true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

export default NumericInput;
